import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
}

export interface PasswordChallenge {
  kind: 'NEW_PASSWORD_REQUIRED';
  user: CognitoUser;
}

function readConfig(): { userPoolId: string; clientId: string } {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!userPoolId || !clientId) {
    throw new Error('Administrator sign-in is not configured for this environment.');
  }
  return { userPoolId, clientId };
}

export function getUserPool(): CognitoUserPool {
  const { userPoolId, clientId } = readConfig();
  return new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
  });
}

function toTokens(session: CognitoUserSession): AuthTokens {
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
  };
}

export function signIn(email: string, password: string): Promise<AuthTokens | PasswordChallenge> {
  const user = new CognitoUser({
    Username: email,
    Pool: getUserPool(),
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: (session) => resolve(toTokens(session)),
      onFailure: (error) => reject(error),
      newPasswordRequired: () =>
        resolve({
          kind: 'NEW_PASSWORD_REQUIRED',
          user,
        }),
    });
  });
}

export function completeNewPassword(user: CognitoUser, newPassword: string): Promise<AuthTokens> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve(toTokens(session)),
        onFailure: (error) => reject(error),
      },
    );
  });
}

export function restoreSession(): Promise<AuthTokens | undefined> {
  try {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      return Promise.resolve(undefined);
    }
    return new Promise((resolve, reject) => {
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        if (error) {
          reject(error);
          return;
        }
        if (!session?.isValid()) {
          resolve(undefined);
          return;
        }
        resolve(toTokens(session));
      });
    });
  } catch {
    return Promise.resolve(undefined);
  }
}

export function signOut(): void {
  try {
    getUserPool().getCurrentUser()?.signOut();
  } catch {
    // Configuration may be missing during local UI development.
  }
}
